import React, { useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import LanguageTabs from './CampaignForm/LanguageTabs'
import {
  AssetClass,
  campaignKeywords,
  generativeCreativeField,
  isCampaignOpen,
  isCampaignPublished,
  isCampaignRunning,
  validateCreative,
  validateLocalizations,
  validateMasterLanguage,
  getLocaleForLanguage,
} from 'api/models'

import { allFilesUploaded } from '../Media/media'
import { augmentIncomingCreativeMedia } from '../Media/mediaPayload'
import Field, { ArrayField } from './CampaignForm/Field'

import DropDown from 'components/Shared/DropDown'
import ErrorMessage from 'components/Shared/ErrorMessage'
import AccordionCard from 'components/Shared/AccordionCard'
import InfoBox from 'components/Shared/InfoBox'
import InfoMessage from 'components/Shared/InfoMessage'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import Textarea from 'components/Shared/Textarea'
import SelectItem from 'components/Shared/SelectItem'
import PortalModal from 'components/Shared/PortalModal'
import ConfirmationModal from 'components/Shared/ConfirmationModal'
import TipDialogueBox from 'components/Shared/TipDialogueBox'

import { getUserReports } from 'api/userReports'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import useHasPermission, { hasPartialServiceAccessTier, Permissions } from 'hooks/useHasPermission'
import { fetchCampaignCtasAsync } from 'api/campaigns'
import googleCallToActionsApi from 'domain/googleCallToActions/api'
import constants, { SUPPORT_ARTICLE_URLS } from '../../../../constants'
import {
  arrayReducer,
  initStateArray,
  initStateObject,
  objectReducer,
  OVERWRITE,
  LOADING,
  FAIL,
} from 'reducers/default'
import {
  codifyAPIError,
  resolveCallToAction,
  snakeCaseToPascalCase,
  treatAsProductEntry,
} from 'utils/helpers'
import { useCampaignSetup, useCampaignSetupDispatch } from '../Context/CampaignSetupContext'
import { translateSetupError } from '../Context/setupError'
import { hasEnabledVariations } from '../Context/disabledVariations'
import { hasAudienceTargeting, hasSavedAudienceTargeting } from '../Context/util'
import MediaUpload from '../Media/MediaUpload'
import AutoFill from './AutoFill'
import { creativeActions, creativeFormReducer } from './CampaignForm/creativeFormReducer'
import { DescriptionField } from './CampaignForm/DescriptionField'
import { Keywords } from './CampaignForm/Keywords'
import Targeting from './CampaignForm/Targeting'
import {
  activeTargetingPlatforms,
  TARGETING_PLATFORMS,
  validateLocationTargeting,
} from './CampaignForm/Targeting/targeting'
import { derivePlatformSelectionFromSetup } from '../platform'
import {
  deriveCampaignTypeFromSetup,
  GoogleCampaignType,
  googleFirstHeadlineCharLimit,
  toApiCampaignType,
} from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'
import { getRemovedField, parseCreatives } from './CampaignForm/utils'
import CoCreate from './CoCreate'
import CarouselAd from '../CarouselAd/CarouselAd'

// Shared cap across FB media and YouTube videos — a YouTube video lives in
// the same media[] array/slot count as everything else, not an unlimited
// extra slot on top of the FB media cap.
const MAX_MEDIA_ITEMS = 10

export default function CampaignForm({
  eventId,
  eventDate,
  eventInfo,
  campaign,
  goal,
  onCancel,
  onUpdate,
  showValidationErrors = false,
  isFirstTc = false,
}) {
  const { t, i18n } = useTranslation()

  const {
    setup,
    loading: setupLoading,
    hasPendingMedia,
    error: setupError,
    canAnonEdit,
  } = useCampaignSetup()
  const { overwriteTCCreatives, isGeneratingForCluster } = useCampaignSetupDispatch()
  const targetingPlatforms = useMemo(
    () => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
    [setup],
  )
  const googleCampaignType = useMemo(() => deriveCampaignTypeFromSetup(setup), [setup])
  const googleApiCampaignType = useMemo(
    () => toApiCampaignType(googleCampaignType),
    [googleCampaignType],
  )
  const googleHeadlineCharLimit = googleFirstHeadlineCharLimit(googleCampaignType)

  const tcTargetingForAudience = useMemo(
    () => setup.targeting?.find((x) => x.tc === campaign.tc),
    [setup.targeting, campaign.tc],
  )
  const hasCustomAudience = useMemo(
    () => hasAudienceTargeting(tcTargetingForAudience),
    [tcTargetingForAudience],
  )
  const savedAudienceSelected = useMemo(
    () => hasSavedAudienceTargeting(tcTargetingForAudience),
    [tcTargetingForAudience],
  )

  const partnerDetails =
    localStorage.getItem('partnerDetails') && JSON.parse(localStorage.getItem('partnerDetails'))
  const isITCRecommendations = ['ITC', 'ITC_v2'].includes(
    partnerDetails && partnerDetails.campaignRecommendationsModel,
  )

  const [validationErrorsMedia, setValidationErrorsMedia] = useState([])
  const errorRef = useRef(null)
  const headlineSectionRef = useRef(null)
  const [textAssetsOpen, setTextAssetsOpen] = useState(true)

  const scrollToHeadlineSection = useCallback(() => {
    const section = headlineSectionRef.current
    if (!section) return

    const scrollAndFocus = () => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })

      const firstHeadlineInput = section.querySelector('#headline-0')
      if (firstHeadlineInput && !firstHeadlineInput.disabled) {
        // Focus after scroll starts; preventScroll keeps smooth scroll from jumping.
        window.setTimeout(() => {
          firstHeadlineInput.focus({ preventScroll: true })
        }, 300)
      }
    }

    if (textAssetsOpen) {
      scrollAndFocus()
    } else {
      // The headline field lives inside the "Text Assets" accordion — open
      // it first and wait for the collapse transition to finish, otherwise
      // scrollIntoView measures a section that's still height:0/hidden.
      setTextAssetsOpen(true)
      window.setTimeout(scrollAndFocus, 350)
    }
  }, [textAssetsOpen])

  const [ctaOptions, dispatchCtaOptionsAction] = useReducer(arrayReducer, initStateArray)
  const [googleCtaOptions, dispatchGoogleCtaOptionsAction] = useReducer(
    arrayReducer,
    initStateArray,
  )

  // state for managing languages
  const [chosenLanguages, setChosenLanguages] = useState([])
  const [activeLanguage, setActiveLanguage] = useState('en')
  const [minBodyLanguages, setMinBodyLanguages] = useState(3)
  const [minHeadlineLanguages, setMinHeadlineLanguages] = useState(3)
  const [invalidLanguageCodes, setInvalidLanguageCodes] = useState([])

  const [tabSwitch, setTabSwitch] = useState('single')
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const [userReports, userReportsDispatch] = useReducer(objectReducer, initStateObject)
  const currentPublishedTargeting = useRef(setup.targeting)
  const hasPrismaAccess = hasPartialServiceAccessTier() || treatAsProductEntry(eventInfo.category)
  const hasWaveAccess = useHasPermission(Permissions.wave)

  const masterLang = chosenLanguages[0]?.value
  const isMaster = activeLanguage === masterLang

  // Ref for tracking last changes in localizations
  const lastLocalizationsRef = useRef({})
  const hasNormalizedInitialLanguage = useRef(false)

  // rebuild localizations in chosen order
  const rebuildLocalizationsInChosenOrder = useCallback((rawLocalizations, chosen) => {
    const ordered = {}
    for (let i = 0; i < chosen.slice(1).length; i++) {
      const lang = chosen.slice(1)[i]
      const key = getLocaleForLanguage(lang.value)
      const existing = rawLocalizations?.[key]
      ordered[key] = existing
        ? {
            headlines: [...(existing.headlines || [])],
            bodies: [...(existing.bodies || [])],
            description: existing.description || '',
            index: i + 1,
          }
        : {
            headlines: ['', '', ''],
            bodies: ['', '', ''],
            description: '',
            index: i + 1,
          }
    }
    return ordered
  }, [])

  const originalCreative = useMemo(() => {
    return parseCreatives(setup, campaign.tc, eventDate, hasPrismaAccess)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, campaign.tc, eventDate])

  const [form, dispatchFormAction] = useReducer(creativeFormReducer, {
    ...initStateObject,
    content: originalCreative,
  })
  const ctaLabel = useCallback(
    (value) =>
      t([
        `Recommendations.campaign.setup.call_to_action.${value}`,
        snakeCaseToPascalCase(value),
      ]),
    [t],
  )
  const buildSortedCtaOptions = useCallback(
    (items) =>
      [...items]
        .map((option) => ({ value: option, text: ctaLabel(option) }))
        .sort((a, b) => {
          const collator = new Intl.Collator(i18n.language, { sensitivity: 'base' })
          return collator.compare(a.text, b.text)
        }),
    [ctaLabel, i18n.language],
  )
  const sortedCtaOptions = buildSortedCtaOptions(ctaOptions.items)
  const sortedGoogleCtaOptions = buildSortedCtaOptions(googleCtaOptions.items)
  // The label comes from the stored value, not from the fetched option list:
  // the list is goal/lead-form-scoped and may not contain a CTA an older
  // campaign was published with, and it is empty while still loading.
  const renderCtaOption = (selectedValue) => (item) => {
    const value = item?.value ?? selectedValue
    return value ? ctaLabel(value) : ''
  }

  // call_to_action on the backend: a {fb, google} object always works,
  // including single-platform (e.g. {fb: "BOOK_NOW"} alone is valid) —
  // confirmed with BE. A plain string only occurs when a single platform is
  // (or was) selected, and belongs to whichever platform is currently
  // active — resolveCallToAction mirrors the backend's own resolution
  // (see its definition in utils/helpers.js), so both dropdowns here read
  // through it rather than duplicating the platform-resolution logic.
  const rawCallToAction = form.content.call_to_action
  const metaCallToActionValue = resolveCallToAction(rawCallToAction, 'fb')
  const googleCallToActionValue = resolveCallToAction(rawCallToAction, 'google')

  // Rebuilds the full call_to_action value from both platforms' current
  // selections as a {fb, google} object, keyed only by the currently active
  // platform(s). Must be computed here — targetingPlatforms only exists in
  // this component, creativeFormReducer has no platform awareness.
  const buildCallToActionValue = useCallback(
    (nextMetaValue, nextGoogleValue) => {
      const value = {}
      if (targetingPlatforms.includes(TARGETING_PLATFORMS.fb)) value.fb = nextMetaValue
      if (targetingPlatforms.includes(TARGETING_PLATFORMS.google)) value.google = nextGoogleValue
      return value
    },
    [targetingPlatforms],
  )

  useEffect(() => {
    getUserReports(userReportsDispatch, eventId, {
      reportType: 'WRONG_TOPIC_RECOMMENDATION',
      tc: campaign.tc,
      tc_run_id: campaign.tc_run_id,
    })
  }, [eventId, campaign])

  const reportedKeywords = useMemo(() => {
    if (userReports.content?.items) {
      return userReports.content?.items.map((item) => ({
        keyword: item.keyword,
        id: item.id,
      }))
    }
    return []
  }, [userReports])

  const onReportChange = useCallback(() => {
    getUserReports(userReportsDispatch, eventId, {
      reportType: 'WRONG_TOPIC_RECOMMENDATION',
      tc: campaign.tc,
      tc_run_id: campaign.tc_run_id,
    })
  }, [eventId, campaign.tc, campaign.tc_run_id, userReportsDispatch])

  // setup.integration_details gets a brand-new array reference every time
  // setupLoaded replaces the whole `setup` object (e.g. after any targeting
  // save, not just an integration_details change) — depending on the array
  // itself below would re-fire both CTA-options fetches (and flash the CTA
  // dropdown to its empty-options fallback) on every unrelated setup reload.
  // A derived boolean is a stable primitive that only actually changes when
  // whether-there's-a-pixel changes, same technique as hasGooglePlatform.
  const hasPixel = useMemo(
    () => setup.integration_details.some((x) => x.asset_class === AssetClass.pixel),
    [setup.integration_details],
  )

  useEffect(() => {
    const controller = new AbortController()
    const leadFormId = setup.lead_form_id
    fetchCampaignCtasAsync(
      dispatchCtaOptionsAction,
      setup.goal ?? goal,
      hasPixel,
      leadFormId,
      controller,
    )

    return () => controller.abort()
  }, [setup.goal, goal, hasPixel, setup.lead_form_id])

  // activeTargetingPlatforms() always builds a brand-new array, and
  // targetingPlatforms recomputes on every `setup` reference change — which
  // happens on every creative edit, not just a platform switch (the
  // reducer's setTCCreatives case reassigns state.setup.creatives on every
  // field change). Depending on the array itself here would re-fire this
  // fetch on every keystroke anywhere in the form; a derived boolean is a
  // stable primitive that only actually changes when Google is
  // added/removed from the platform selection.
  const hasGooglePlatform = targetingPlatforms.includes(TARGETING_PLATFORMS.google)
  const hasBothPlatforms = hasGooglePlatform && targetingPlatforms.includes(TARGETING_PLATFORMS.fb)

  // Carousel ads are supported by Google, but only for Demand Gen campaigns
  // (not Display) — see requiredSlotRatios.js for the same platform +
  // campaignType gating pattern applied to media slot ratios.
  const googleBlocksCarousel =
    hasGooglePlatform && googleCampaignType !== GoogleCampaignType.demandGen

  useEffect(() => {
    if (!hasGooglePlatform) return undefined

    let cancelled = false
    const leadFormId = setup.lead_form_id

    dispatchGoogleCtaOptionsAction({ type: LOADING })
    googleCallToActionsApi
      .fetchCallToActionsAsync(setup.goal ?? goal, hasPixel, leadFormId, googleApiCampaignType)
      .then((response) => {
        if (cancelled) return
        if (response.error) {
          dispatchGoogleCtaOptionsAction({ type: FAIL, payload: codifyAPIError(response) })
          return
        }
        dispatchGoogleCtaOptionsAction({
          type: OVERWRITE,
          payload: {
            total: response.data.length,
            page: 0,
            maxPage: response.data.length,
            items: response.data,
          },
        })
      })

    return () => {
      cancelled = true
    }
  }, [hasGooglePlatform, setup.goal, goal, hasPixel, setup.lead_form_id, googleApiCampaignType])

  useEffect(() => {
    // This is needed as defaults are assigned to some fields (cta, destination_url) by the context.
    // originalCreative is re-derived from `setup`, which is itself rewritten
    // on every field change (see handleChange/overwriteTCCreatives below), so
    // this effectively just re-syncs form.content with what's already there —
    // including picking up anything that changed elsewhere (e.g.
    // destination_url/call_to_action propagated in from another TC's save).
    dispatchFormAction({ type: OVERWRITE, payload: originalCreative })
  }, [originalCreative])

  useEffect(() => {
    if (form.content.carousel_cards?.length > 0) {
      setTabSwitch('carousel')
    }
  }, [form.content.carousel_cards])

  const keywords = useMemo(() => campaignKeywords(campaign), [campaign])

  useEffect(() => {
    if ((form.error || setupError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [form.error, errorRef, setupError])

  // when the component mounts this sets mobile nav to display none
  // the setup started buttons were not showing above the navbar on some browser/ devices
  useEffect(() => {
    const mobileNav = document.getElementById('mobile-navbar')
    if (mobileNav?.style) {
      mobileNav.style.display = 'none'
    }

    return () => {
      const mobileNav = document.getElementById('mobile-navbar')
      if (mobileNav?.style) {
        mobileNav.style.display = 'block'
      }
    }
  }, [])

  const isRunning = isCampaignRunning(campaign, { validateNotEnded: true })
  const isPublished = isCampaignPublished(campaign)
  const isOpen = isCampaignOpen(campaign)
  const isGeneratingMedia = isGeneratingForCluster(campaign.tc, campaign.tc_run_id)
  const FieldError = ({ message }) => {
    if (!message) return null

    // Get media support article URL
    const mediaSupportArticleUrl =
      SUPPORT_ARTICLE_URLS?.media?.[i18n.language] || SUPPORT_ARTICLE_URLS?.media?.en

    // Helper function to render error message with links
    const renderErrorMessage = (errorMsg) => {
      if (!errorMsg) return null

      // Check if message contains <a> tag
      if (errorMsg.includes('<a>')) {
        // Split message by <a> tags and render with proper link component
        const parts = errorMsg.split(/(<a>.*?<\/a>)/g)
        return (
          <>
            {parts.map((part, idx) => {
              if (part.startsWith('<a>') && part.endsWith('</a>')) {
                // Extract link text (content between <a> and </a>)
                const linkText = part.replace(/<a>|<\/a>/g, '')
                return (
                  <a
                    key={idx}
                    href={mediaSupportArticleUrl}
                    className="infobox-tooltip__link"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {linkText}
                  </a>
                )
              }
              return <span key={idx}>{part}</span>
            })}
          </>
        )
      }
      // If no link, return plain text
      return errorMsg
    }

    // Handle case where `message` is a single string
    if (typeof message === 'string') {
      return <div className="warning-red">{renderErrorMessage(message)}</div>
    }

    // Handle case where `message` is an array of strings
    if (Array.isArray(message) && message.filter((item) => item).length > 0) {
      return (
        <div className="warning-red mb-2">
          {message.map((err, idx) => (
            <ErrorMessage
              variant="light"
              style={{ justifyContent: 'start', marginTop: '0', marginBottom: '6px' }}
              key={idx}
            >
              {renderErrorMessage(err)}
            </ErrorMessage>
          ))}
        </div>
      )
    }

    return <div className="warning-red">{message}</div>
  }

  FieldError.propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  }

  // functions for working with localizations
  const getLocalizationsFromForm = useCallback(() => {
    return form?.content?.localizations || {}
  }, [form?.content?.localizations])

  const handleChange = useCallback(
    (action, form, hasPendingMedia = false) => {
      dispatchFormAction(action)
      const newFields = creativeFormReducer(form, action).content
      const removedField = getRemovedField(form.content, newFields, action)

      overwriteTCCreatives({
        tc: campaign.tc,
        tc_run_id: campaign.tc_run_id,
        creatives: newFields,
        hasPendingMedia,
        creativeAction: {
          action,
          removedFields: [removedField],
        },
      })
    },
    [campaign.tc, campaign.tc_run_id, overwriteTCCreatives],
  )

  const handleLanguagesChanged = (newLanguages) => {
    setChosenLanguages(newLanguages)

    // first language - main fields, others - localizations
    const otherLanguages = newLanguages.slice(1)

    // update localizations only for other languages
    const currentLocalizations = getLocalizationsFromForm()
    const newLocalizations = { ...currentLocalizations }

    // remove localizations for languages that are no longer selected (except first)
    const otherLanguageCodes = otherLanguages.map((lang) => lang.value)
    Object.keys(newLocalizations).forEach((locale) => {
      const langCode = locale.split('_')[0]
      if (!otherLanguageCodes.includes(langCode)) {
        delete newLocalizations[locale]
      }
    })

    // add empty localizations for new languages (except first)
    const masterFields = getCurrentLanguageFields()
    otherLanguages.forEach((lang, index) => {
      const locale = getLocaleForLanguage(lang.value)
      if (!newLocalizations[locale]) {
        newLocalizations[locale] = {
          headlines: Array(
            masterFields.headline.length > 3 ? masterFields.headline.length : 3,
          ).fill(''),
          bodies: Array(masterFields.body.length > 3 ? masterFields.body.length : 3).fill(''),
          description: '',
          index: index + 1,
        }
      }
    })

    // Rebuild in chosen order
    const orderedLocalizations = rebuildLocalizationsInChosenOrder(newLocalizations, newLanguages)

    lastLocalizationsRef.current = orderedLocalizations

    handleChange(
      { type: creativeActions.localizationsUpdate, payload: orderedLocalizations },
      form,
      false,
    )
  }

  const handleActiveLanguageChange = useCallback((newLanguage) => {
    setActiveLanguage(newLanguage)
  }, [])

  const handleBatchChange = useCallback(
    (actions, hasPendingMedia = false) => {
      let workingState = form
      for (const action of actions) {
        workingState = creativeFormReducer(workingState, action)
      }

      // trim localizations to the same length as the master language
      if (workingState.content.localizations) {
        const masterFilledCount = {
          body: (workingState.content.bodies || workingState.content.body || []).filter(Boolean)
            .length,
          headline: (workingState.content.headlines || workingState.content.headline || []).filter(
            Boolean,
          ).length,
        }

        const trimmedLocalizations = Object.fromEntries(
          Object.entries(workingState.content.localizations).map(([locale, data]) => [
            locale,
            {
              ...data,
              bodies: (data.bodies || []).slice(0, masterFilledCount.body),
              headlines: (data.headlines || []).slice(0, masterFilledCount.headline),
            },
          ]),
        )

        workingState.content.localizations = trimmedLocalizations
      }

      dispatchFormAction({ type: OVERWRITE, payload: workingState.content })

      if (workingState.content.localizations) {
        lastLocalizationsRef.current = { ...workingState.content.localizations }
      }

      const removedField = getRemovedField(
        form.content,
        workingState.content,
        actions[actions.length - 1],
      )

      overwriteTCCreatives({
        tc: campaign.tc,
        tc_run_id: campaign.tc_run_id,
        creatives: workingState.content,
        hasPendingMedia,
        creativeAction: {
          action: actions[actions.length - 1],
          removedFields: [removedField],
        },
      })
    },
    [form, campaign.tc, campaign.tc_run_id, overwriteTCCreatives],
  )

  const updateLanguageField = useCallback(
    (language, fieldType, value) => {
      const firstLanguage = chosenLanguages[0]
      const isFirstLanguage = firstLanguage && language === firstLanguage.value

      if (isFirstLanguage) {
        const actionType =
          fieldType === 'body'
            ? 'bodyUpdate'
            : fieldType === 'headline'
              ? 'headlineUpdate'
              : 'descriptionUpdate'

        const action = {
          type: creativeActions[actionType],
          payload: value,
        }

        handleChange(action, form, false)
      } else {
        // Processing other languages (LOCALIZATIONS)
        const locale = getLocaleForLanguage(language)
        const fieldKey =
          fieldType === 'body' ? 'bodies' : fieldType === 'headline' ? 'headlines' : 'description'

        // Получаем текущие localizations из form.content
        const currentLocalizations = form?.content?.localizations || {}

        // get base localization (from form.content or from lastLocalizationsRef)
        const baseLocalization = lastLocalizationsRef.current[locale] ||
          currentLocalizations[locale] || {
            headlines: ['', '', ''],
            bodies: ['', '', ''],
            description: '',
            index: 1,
          }

        // create new localization with updated field
        const newLocalization = {
          ...baseLocalization,
          [fieldKey]: value,
        }

        // create new localizations for dispatch (deep copy to avoid read-only issues)
        const newLocalizations = {
          ...currentLocalizations,
          [locale]: { ...newLocalization },
        }

        // update ref for next call (create new object to avoid read-only issues)
        lastLocalizationsRef.current = { ...newLocalizations }

        handleChange(
          { type: creativeActions.localizationsUpdate, payload: newLocalizations },
          form,
          false,
        )
      }
    },
    [dispatchFormAction, chosenLanguages, form?.content?.localizations, handleChange, form],
  )

  function getCurrentLanguageFields() {
    // if it is the first language (first tab), return main fields. Also
    // covers a legacy creative with no language at all (chosenLanguages stays
    // empty — see the effect above gating on form.content.language) — there's
    // no other language to look up, so fall back to main fields rather than
    // calling getLocaleForLanguage(null) below and crashing.
    const firstLanguage = chosenLanguages[0]
    if (!firstLanguage || activeLanguage === firstLanguage.value) {
      return {
        body: [...(form?.content?.body || [''])],
        headline: [...(form?.content?.headline || [''])],
        description: form?.content?.description || '',
      }
    }

    // for other languages take from localizations with last changes from ref
    const locale = getLocaleForLanguage(activeLanguage)
    const localization = form?.content?.localizations?.[locale] || {
      headlines: ['', '', ''],
      bodies: ['', '', ''],
      description: '',
    }

    // merge with last changes from ref
    const lastLocalization = lastLocalizationsRef.current[locale]

    const mergedLocalization = {
      ...localization,
      ...lastLocalization,
    }

    return {
      body: [...(mergedLocalization.bodies || [''])],
      headline: [...(mergedLocalization.headlines || [''])],
      description: mergedLocalization.description || '',
    }
  }

  // Get count of filled fields in master language
  const getMasterFilledCount = useCallback(() => {
    const filledCount = {
      body: (form?.content?.bodies || form?.content?.body || []).filter(Boolean).length,
      headline: (form?.content?.headlines || form?.content?.headline || []).filter(Boolean).length,
    }
    return filledCount
  }, [
    form?.content?.bodies,
    form?.content?.body,
    form?.content?.headlines,
    form?.content?.headline,
  ])

  useEffect(() => {
    if (masterLang) {
      const masterFields = getCurrentLanguageFields()
      setMinBodyLanguages(masterFields.body.length)
      setMinHeadlineLanguages(masterFields.headline.length)
    }
  }, [masterLang, form?.content?.bodies, form?.content?.headlines])

  useEffect(() => {
    if (form?.content?.language && form.success) {
      const languagesFromForm = []

      // Add main language
      languagesFromForm.push(form.content.language)

      // Add languages from localizations sorted by index
      if (form.content.localizations) {
        const sortedEntries = Object.entries(form.content.localizations).sort(
          ([, a], [, b]) => (a.index || 0) - (b.index || 0),
        )

        sortedEntries.forEach(([locale]) => {
          const langCode = locale.split('_')[0]
          if (!languagesFromForm.includes(langCode)) {
            languagesFromForm.push(langCode)
          }
        })
      }

      // Convert to needed format
      const formattedLanguages = languagesFromForm.map((language) => ({
        text: `common.language.${language}`,
        value: language,
      }))

      // update state
      setChosenLanguages(formattedLanguages)
    }
  }, [form?.content?.language, form?.content?.localizations, form.success])

  useEffect(() => {
    const defaultLang = form.content.language

    // initialize activeLanguage
    setActiveLanguage(defaultLang)

    if (form?.content?.localizations) {
      lastLocalizationsRef.current = { ...form.content.localizations }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Normalize initial language to 'cs' if it is 'cz'
  useEffect(() => {
    if (!hasNormalizedInitialLanguage.current && form?.content?.language === 'cz') {
      hasNormalizedInitialLanguage.current = true
      handleChange({ type: creativeActions.cocreateLanguageUpdate, payload: 'cs' }, form, false)
      setActiveLanguage('cs')
    }
  }, [form, form?.content?.language, handleChange])

  // Validate localizations and track invalid language codes
  useEffect(() => {
    const invalidCodes = []

    // Validate master language
    const masterValidation = validateMasterLanguage(form.content)
    if (!masterValidation.valid) {
      invalidCodes.push(...masterValidation.invalidLocales)
    }

    // Check localizations
    if (form?.content?.localizations && typeof form.content.localizations === 'object') {
      const masterFilledCount = getMasterFilledCount()
      const validationResult = validateLocalizations(form.content, masterFilledCount)

      if (!validationResult.valid) {
        // Convert locale codes (e.g., "de_DE") to language codes (e.g., "de")
        const invalidLocaleCodes = validationResult.invalidLocales.map(
          (locale) => locale.split('_')[0],
        )
        invalidCodes.push(...invalidLocaleCodes)
      }
    }

    setInvalidLanguageCodes(invalidCodes)
  }, [
    form?.content?.localizations,
    form?.content?.headline,
    form?.content?.body,
    form?.content?.description,
    form?.content?.language,
    getMasterFilledCount,
  ])

  const handleSwitchMediaMode = (tabSwitchTo) => {
    if (tabSwitch === tabSwitchTo) return

    if (tabSwitchTo === 'carousel' && form.content.media.length > 0) {
      setIsConfirmModalOpen(true)
    } else if (tabSwitchTo === 'single' && form.content.carousel_cards?.length > 0) {
      setIsConfirmModalOpen(true)
    } else {
      setTabSwitch(tabSwitchTo)
      handleChange(
        {
          type: creativeActions.switchToCarousel,
        },
        form,
      )
    }
  }

  const handleCarouselCardsChange = useCallback(
    (cards) => {
      handleChange(
        {
          type: creativeActions.carouselCardsSet,
          payload: cards,
        },
        form,
      )
    },
    [form, handleChange],
  )

  const handleCarouselLanguageChange = useCallback(
    (lang) => {
      setActiveLanguage(lang)
      handleChange({ type: creativeActions.carouselLanguageUpdate, payload: lang }, form)
    },
    [form, handleChange],
  )

  // return
  return (
    <>
      <div ref={errorRef} className="py-0" data-testid="campaign-form-error-banner">
        {(form.error || setupError) && (
          <ErrorMessage danger>
            {form.message ? t(form.message) : translateSetupError(t, setupError)}
          </ErrorMessage>
        )}
      </div>
      <form className="campaign-form width-100" data-testid="campaign-form-test-id">
        <div className="d-flex flex-column justify-content-center" style={{ marginBottom: '32px' }}>
          <Keywords
            eventId={eventId}
            setupProcessId={setup.id}
            campaign={campaign}
            isCampaignRunning={isRunning}
            reportedKeywords={reportedKeywords}
            keywords={keywords.headline}
            onReportChange={onReportChange}
          />
        </div>

        <p className="ad-type-label">{t('Recommendations.campaign.setup.adType')}</p>

        <InfoBox
          body={
            googleBlocksCarousel
              ? t('Tips.Recommendations.CreativeComposer.mediaSwitchDisabledGoogle.body')
              : isGeneratingMedia
                ? t('Tips.Recommendations.CreativeComposer.mediaSwitchDisabledGenerating.body')
                : t('Tips.Recommendations.CreativeComposer.mediaSwitchDisabled.body')
          }
          enabled={isRunning || !canAnonEdit || isGeneratingMedia || googleBlocksCarousel}
          withAnchor={false}
          classNames="media-switch-infobox"
        >
          <div className="media-switch-container">
            <SelectItem
              name="media-switch"
              id="media-switch-single"
              checked={tabSwitch === 'single'}
              onChange={() => handleSwitchMediaMode('single')}
              disabled={isRunning || !canAnonEdit || isGeneratingMedia || googleBlocksCarousel}
              title={t('Recommendations.campaign.setup.mediaSwitch.single')}
            />
            <SelectItem
              name="media-switch"
              id="media-switch-carousel"
              checked={tabSwitch === 'carousel'}
              onChange={() => handleSwitchMediaMode('carousel')}
              disabled={isRunning || !canAnonEdit || isGeneratingMedia || googleBlocksCarousel}
              title={t('Recommendations.campaign.setup.mediaSwitch.carousel')}
            />
          </div>
        </InfoBox>

        <div className={`d-flex flex-column ${tabSwitch === 'carousel' ? 'flex-column-reverse' : ''}`}>
          <AccordionCard title="Text Assets" isOpen={textAssetsOpen} onToggle={setTextAssetsOpen}>
            {tabSwitch === 'single' && (
              <>
                {hasGooglePlatform && (
                  <TipDialogueBox
                    text={t('Recommendations.campaign.setup.googleLocalizationWarning')}
                    className="mt-1"
                  />
                )}

                <LanguageTabs
                  activeLanguage={activeLanguage}
                  chosenLanguages={chosenLanguages}
                  onLanguageChange={handleActiveLanguageChange}
                  mode="edit"
                  invalidLocales={showValidationErrors ? invalidLanguageCodes : []}
                  leadFormId={setup.lead_form_id}
                  onLanguageAdd={(newLanguage) => {
                    if (chosenLanguages.length >= 5) return
                    const updatedLanguages = [...chosenLanguages, newLanguage]
                    handleLanguagesChanged(updatedLanguages)
                    handleActiveLanguageChange(newLanguage.value)
                  }}
                  onLanguageRemove={(languageToRemove) => {
                    if (chosenLanguages.length <= 1) return

                    const languageToRemoveIndex = chosenLanguages.findIndex(
                      (lang) => lang.value === languageToRemove,
                    )
                    const isRemovingFirstLanguage = languageToRemoveIndex === 0

                    let updatedLanguages = chosenLanguages.filter(
                      (lang) => lang.value !== languageToRemove,
                    )

                    // If removing first language, make second language the new main language
                    if (isRemovingFirstLanguage && updatedLanguages.length > 0) {
                      const newMainLanguage = updatedLanguages[0].value
                      const newMainLocale = getLocaleForLanguage(newMainLanguage)

                      // Get current localizations
                      const currentLocalizations = getLocalizationsFromForm()
                      const newMainLocalization = currentLocalizations[newMainLocale] || {
                        headlines: ['', '', ''],
                        bodies: ['', '', ''],
                        description: '',
                      }

                      // Remove new main language from localizations
                      const updatedLocalizations = { ...currentLocalizations }
                      delete updatedLocalizations[newMainLocale]

                      // Update lastLocalizationsRef immediately
                      lastLocalizationsRef.current = updatedLocalizations

                      // Use single action to change main language
                      handleChange(
                        {
                          type: creativeActions.changeMainLanguage,
                          payload: {
                            newMainLanguage,
                            newMainLocalization,
                            updatedLocalizations,
                          },
                        },
                        form,
                        false,
                      )
                    } else {
                      // Removing a non-first language → delete its localization and send to backend
                      const localeToRemove = getLocaleForLanguage(languageToRemove)
                      const currentLocalizations = getLocalizationsFromForm()
                      if (currentLocalizations[localeToRemove]) {
                        const updatedLocalizations = { ...currentLocalizations }
                        delete updatedLocalizations[localeToRemove]
                        // sync ref and send to backend
                        lastLocalizationsRef.current = updatedLocalizations
                        handleChange(
                          {
                            type: creativeActions.localizationsUpdate,
                            payload: updatedLocalizations,
                          },
                          form,
                          false,
                        )
                      }
                    }

                    // Update languages list and active language
                    setChosenLanguages(updatedLanguages)
                    if (activeLanguage === languageToRemove) {
                      setActiveLanguage(updatedLanguages[0].value)
                    }
                  }}
                  isGoogleOnly={
                    hasGooglePlatform && !targetingPlatforms.includes(TARGETING_PLATFORMS.fb)
                  }
                />
              </>
            )}

            <div className={`mb-5 campaign-form__section ${tabSwitch === 'carousel' ? 'pb-2' : ''}`}>
              {/* CoCreate for active language */}
              {(isOpen || isRunning) && tabSwitch === 'single' && canAnonEdit && (
                <div className="mb-4">
                  <CoCreate
                    eventId={eventId}
                    tc={form?.content?.tc}
                    tc_run_id={form?.content?.tc_run_id}
                    fields={(() => {
                      if (isMaster) {
                        return {
                          bodies:
                            getCurrentLanguageFields().body.length > 3
                              ? getCurrentLanguageFields().body
                              : Array(3).fill(''),
                          headlines:
                            getCurrentLanguageFields().headline.length > 3
                              ? getCurrentLanguageFields().headline
                              : Array(3).fill(''),
                          description: getCurrentLanguageFields().description,
                        }
                      } else {
                        // For non-master languages, pass master's field count
                        const masterFilledCount = getMasterFilledCount()
                        return {
                          bodies: Array(masterFilledCount.body).fill(''),
                          headlines: Array(masterFilledCount.headline).fill(''),
                          description: '',
                        }
                      }
                    })()}
                    promptForConfirmation={getCurrentLanguageFields().body.filter(Boolean).length > 0}
                    onChange={(generatedContent) => {
                      if (activeLanguage === chosenLanguages[0]?.value) {
                        handleChange(
                          { type: creativeActions.bodyHeadlineUpdate, payload: generatedContent },
                          form,
                          false,
                        )
                      } else {
                        updateLanguageField(activeLanguage, 'headline', generatedContent.headline)
                        updateLanguageField(activeLanguage, 'body', generatedContent.body)
                        updateLanguageField(
                          activeLanguage,
                          'description',
                          generatedContent.description?.[0] || '',
                        )
                      }
                    }}
                    language={activeLanguage}
                    onLanguageChanged={(lang) => {
                      const languageExists = chosenLanguages.find((l) => l.value === lang)

                      if (languageExists) {
                        // if language exists, switch to it
                        handleActiveLanguageChange(lang)
                      } else {
                        // replace current active tab with new language
                        const currentTabIndex = chosenLanguages.findIndex(
                          (l) => l.value === activeLanguage,
                        )

                        if (currentTabIndex !== -1) {
                          // create new language object
                          const newLanguage = {
                            text: `common.language.${lang}`,
                            value: lang,
                          }

                          // replace current tab with new language
                          const updatedLanguages = [...chosenLanguages]
                          updatedLanguages[currentTabIndex] = newLanguage

                          // update list of languages and switch to new language
                          handleLanguagesChanged(updatedLanguages)
                          handleActiveLanguageChange(lang)

                          if (currentTabIndex === 0) {
                            handleChange(
                              { type: creativeActions.cocreateLanguageUpdate, payload: lang },
                              form,
                              false,
                            )
                          }
                        }
                      }
                    }}
                    className={`${isITCRecommendations && isOpen ? 'with-border' : ''}`}
                    chosenLanguages={chosenLanguages}
                    platforms={targetingPlatforms}
                    campaignType={googleApiCampaignType}
                  />
                </div>
              )}

              {/* Fields for active language */}
              <div className="language-content">
                <ArrayField
                  as={Textarea}
                  fieldName="body"
                  form={getCurrentLanguageFields()}
                  isDisabled={(index) => {
                    const isCurrentlyMaster = activeLanguage === masterLang
                    const filledCount = getMasterFilledCount().body
                    return !isCurrentlyMaster && index >= filledCount
                  }}
                  onChange={(payload, index) => {
                    const current = getCurrentLanguageFields()
                    const prevLen = current.body.length
                    let next = [...current.body]

                    if (payload === null) next.splice(index, 1)
                    else if (typeof index !== 'undefined' && index != null) next[index] = payload
                    else next = payload

                    if (isMaster) {
                      const actions = [{ type: creativeActions.bodyUpdate, payload: next }]

                      if (next.length !== prevLen) {
                        const currentLocalizations = getLocalizationsFromForm()
                        const updatedLocalizations = Object.fromEntries(
                          Object.entries(currentLocalizations).map(([locale, data]) => {
                            const curr = Array.isArray(data.bodies) ? data.bodies : []
                            const need = next.length
                            const resized =
                              curr.length > need
                                ? curr.slice(0, need)
                                : [...curr, ...Array(Math.max(0, need - curr.length)).fill('')]
                            return [locale, { ...data, bodies: resized }]
                          }),
                        )
                        actions.push({
                          type: creativeActions.localizationsUpdate,
                          payload: updatedLocalizations,
                        })
                      }

                      handleBatchChange(actions, false)
                      setMinBodyLanguages(next.length)
                    } else {
                      updateLanguageField(activeLanguage, 'body', next)
                    }
                  }}
                  isAddingEnabled={isMaster && !isRunning && canAnonEdit}
                  isRemovingEnabled={isMaster && !isRunning && canAnonEdit}
                  inputChildren={(index) =>
                    index === 0 && (isOpen || isRunning) && canAnonEdit && canAnonEdit ? (
                      <AutoFill
                        eventId={eventId}
                        tc={form?.content?.tc}
                        tc_run_id={form?.content?.tc_run_id}
                        language={activeLanguage}
                        creativeFieldName={generativeCreativeField.bodies}
                        fields={(() => {
                          if (isMaster) {
                            return getCurrentLanguageFields().body.length > 3
                              ? getCurrentLanguageFields().body
                              : Array(3).fill('')
                          } else {
                            // For non-master languages, pass master's field count
                            const masterFilledCount = getMasterFilledCount()
                            return Array(masterFilledCount.body).fill('')
                          }
                        })()}
                        promptForConfirmation={
                          getCurrentLanguageFields().body.filter(Boolean).length > 0
                        }
                        onChange={(generatedContent) => {
                          updateLanguageField(activeLanguage, 'body', generatedContent)
                        }}
                        platforms={targetingPlatforms}
                        campaignType={googleApiCampaignType}
                      />
                    ) : null
                  }
                  restFn={(index) => ({
                    wrapperClass:
                      index === 0 && (isOpen || isRunning) ? 'generative-input align-bottom' : '',
                    inputClass: index === 0 ? 'textarea-autofill' : '',
                  })}
                  minCount={isMaster || minBodyLanguages < 3 ? 3 : minBodyLanguages}
                  maxLength={hasGooglePlatform ? 90 : 280}
                  maxLengthClassNamesFn={(length) =>
                    length > 0 && length <= 200
                      ? 'green'
                      : length > 200 && length <= 250
                        ? 'orange'
                        : length > 250
                          ? 'red'
                          : ''
                  }
                  tooltip={{
                    title: t('Recommendations.campaign.setup.body.title'),
                    body: (
                      <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                        <p className="pb-2">{t('Tips.Recommendations.CreativeComposer.Body.1')}</p>

                        <p>{t('Tips.Recommendations.CreativeComposer.Body.2')}</p>

                        {isPublished && (
                          <p className="mt-2">{t('Tips.Recommendations.CreativeComposer.Body.3')}</p>
                        )}
                      </div>
                    ),
                  }}
                >
                </ArrayField>

                {tabSwitch === 'single' && (
                  <ArrayField
                    sectionRef={headlineSectionRef}
                    fieldName="headline"
                    form={getCurrentLanguageFields()}
                    isDisabled={(index) => {
                      const isCurrentlyMaster = activeLanguage === masterLang
                      const filledCount = getMasterFilledCount().headline
                      return !isCurrentlyMaster && index >= filledCount
                    }}
                    onChange={(payload, index) => {
                      const current = getCurrentLanguageFields()
                      const prevLen = current.headline.length
                      let next = [...current.headline]

                      if (payload === null) next.splice(index, 1)
                      else if (typeof index !== 'undefined' && index != null) next[index] = payload
                      else next = payload

                      if (isMaster) {
                        const actions = [{ type: creativeActions.headlineUpdate, payload: next }]

                        if (next.length !== prevLen) {
                          const currentLocalizations = getLocalizationsFromForm()
                          const updatedLocalizations = Object.fromEntries(
                            Object.entries(currentLocalizations).map(([locale, data]) => {
                              const curr = Array.isArray(data.headlines) ? data.headlines : []
                              const need = next.length
                              const resized =
                                curr.length > need
                                  ? curr.slice(0, need)
                                  : [...curr, ...Array(Math.max(0, need - curr.length)).fill('')]
                              return [locale, { ...data, headlines: resized }]
                            }),
                          )

                          actions.push({
                            type: creativeActions.localizationsUpdate,
                            payload: updatedLocalizations,
                          })
                        }

                        handleBatchChange(actions, false)
                        setMinHeadlineLanguages(next.length)
                      } else {
                        updateLanguageField(activeLanguage, 'headline', next)
                      }
                    }}
                    isAddingEnabled={isMaster && !isRunning && canAnonEdit}
                    isRemovingEnabled={isMaster && !isRunning && canAnonEdit}
                    inputChildren={(index) =>
                      index === 0 ? (
                        <>
                          {(isOpen || isRunning) && canAnonEdit ? (
                            <AutoFill
                              eventId={eventId}
                              tc={form?.content?.tc}
                              tc_run_id={form?.content?.tc_run_id}
                              language={activeLanguage}
                              creativeFieldName={generativeCreativeField.headlines}
                              fields={(() => {
                                if (isMaster) {
                                  return getCurrentLanguageFields().headline.length > 3
                                    ? getCurrentLanguageFields().headline
                                    : Array(3).fill('')
                                } else {
                                  // For non-master languages, pass master's field count
                                  const masterFilledCount = getMasterFilledCount()
                                  return Array(masterFilledCount.headline).fill('')
                                }
                              })()}
                              promptForConfirmation={
                                getCurrentLanguageFields().headline.filter(Boolean).length > 0
                              }
                              onChange={(generatedContent) => {
                                updateLanguageField(activeLanguage, 'headline', generatedContent)
                              }}
                              platforms={targetingPlatforms}
                              campaignType={googleApiCampaignType}
                            />
                          ) : null}
                          {hasGooglePlatform &&
                            (() => {
                              const firstHeadline = getCurrentLanguageFields().headline[0] || ''
                              const variant = !firstHeadline.trim()
                                ? 'info'
                                : firstHeadline.length > googleHeadlineCharLimit
                                  ? 'warning'
                                  : 'success'
                              return (
                                <InfoMessage
                                  variant={variant}
                                  className="mt-1"
                                  text={t('Recommendations.campaign.setup.headline.googleWarning', {
                                    limit: googleHeadlineCharLimit,
                                  })}
                                />
                              )
                            })()}
                        </>
                      ) : null
                    }
                    restFn={(index) => ({
                      wrapperClass: index === 0 && (isOpen || isRunning) ? 'generative-input' : '',
                      inputClass: index === 0 ? 'input-autofill' : '',
                      maxLength: index === 0 && hasGooglePlatform ? googleHeadlineCharLimit : 80,
                      required: index === 0 && hasGooglePlatform,
                    })}
                    minCount={isMaster || minHeadlineLanguages < 3 ? 3 : minHeadlineLanguages}
                    maxLengthClassNamesFn={(length) =>
                      length > 0 && length <= 40
                        ? 'green'
                        : length > 40 && length <= 60
                          ? 'orange'
                          : length > 60
                            ? 'red'
                            : ''
                    }
                    tooltip={{
                      title: t('Recommendations.campaign.setup.headline.title'),
                      body: (
                        <div style={{ fontSize: '.875rem', fontWeight: 500 }}>
                          <p className="pb-1">
                            {t('Tips.Recommendations.CreativeComposer.Headline.1')}
                          </p>

                          <p>{t('Tips.Recommendations.CreativeComposer.Headline.2')}</p>

                          {isPublished && (
                            <p className="mt-2">
                              {t('Tips.Recommendations.CreativeComposer.Headline.3')}
                            </p>
                          )}
                        </div>
                      ),
                    }}
                  >
                  </ArrayField>
                )}

                {tabSwitch === 'single' && targetingPlatforms.includes(TARGETING_PLATFORMS.fb) && (
                  <div className="mb-1">
                    <DescriptionField
                      form={getCurrentLanguageFields()}
                      isCampaignPublished={isPublished}
                      wrapperClass={isOpen || isRunning ? 'generative-input' : ''}
                      label={'meta'}
                      bothSelected={hasBothPlatforms}
                      inputChildren={() =>
                        isOpen || isRunning ? (
                          <AutoFill
                            eventId={eventId}
                            tc={form?.content?.tc}
                            tc_run_id={form?.content?.tc_run_id}
                            language={activeLanguage}
                            creativeFieldName={generativeCreativeField.description}
                            fields={[getCurrentLanguageFields().description].filter(Boolean)}
                            promptForConfirmation={getCurrentLanguageFields().description.length > 0}
                            onChange={(generatedContent) => {
                              updateLanguageField(
                                activeLanguage,
                                'description',
                                generatedContent[0] || 'description',
                              )
                            }}
                            platforms={targetingPlatforms}
                            campaignType={googleApiCampaignType}
                          />
                        ) : null
                      }
                      onChange={(payload) => {
                        updateLanguageField(activeLanguage, 'description', payload)
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <ArrayField
              fieldName="destination_url"
              form={form.content}
              minCount={1}
              maxCount={tabSwitch === 'carousel' ? 1 : undefined}
              onChange={(payload, index) =>
                handleChange(
                  {
                    type: creativeActions.urlUpdate,
                    index,
                    payload: payload !== null ? payload.trim() : null,
                  },
                  form,
                )
              }
              isAddingEnabled={!isRunning && canAnonEdit}
              isRemovingEnabled={!isRunning && canAnonEdit}
              removeBtnClassNames="input__remove--r"
              maxLength={800}
              maxLengthClassNamesFn={(length) =>
                length > 0 && length <= 600
                  ? 'green'
                  : length > 600 && length <= 700
                    ? 'orange'
                    : length > 700
                      ? 'red'
                      : ''
              }
              validator={(val) => {
                return {
                  isValid: val && constants.URL_VALIDATION_REGEX.test(val.trim()),
                  errorMsg: t('common.errors.wrongUrlFormat'),
                }
              }}
              showPositiveFeedback={!isRunning && !isPublished}
              placeholder="https://www.your-website.com"
              validateOnBlur
              tooltip={{
                title:
                  tabSwitch === 'carousel'
                    ? t('Tips.Recommendations.CreativeComposer.DestinationUrlCarousel.title')
                    : t('Tips.Recommendations.CreativeComposer.DestinationUrl.title'),
                body:
                  tabSwitch === 'carousel' ? (
                    t('Tips.Recommendations.CreativeComposer.DestinationUrlCarousel.body')
                  ) : (
                    <>
                      <ol
                        style={{
                          listStyle: 'decimal',
                          paddingLeft: '1.4rem',
                          fontSize: '.875rem',
                          fontWeight: 500,
                        }}
                      >
                        <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.1')}</li>
                        <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.2')}</li>
                        <li>{t('Tips.Recommendations.CreativeComposer.DestinationUrl.3')}</li>
                      </ol>

                      {isPublished && (
                        <p className="mt-2" style={{ fontSize: '.875rem', fontWeight: 500 }}>
                          {t('Tips.Recommendations.CreativeComposer.DestinationUrl.4')}
                        </p>
                      )}
                    </>
                  ),
              }}
              wrapperClass={
                isRunning ? 'input-cell--input-padding-min' : 'input-cell--input-padding-max'
              }
            />

            <div className="creative-composer-section-wrapper cta-wrapper">
              {targetingPlatforms.includes(TARGETING_PLATFORMS.fb) && (
                <Field
                  fieldName="call_to_action"
                  as={DropDown}
                  label={'meta'}
                  bothSelected={hasBothPlatforms}
                  tooltip={{ content: 'Tips.Recommendations.CreativeComposer.CallToAction' }}
                  value={metaCallToActionValue}
                  options={sortedCtaOptions.map((x) => ({ value: x.value, text: x.text }))}
                  onChange={(payload) => {
                    const nextValue = buildCallToActionValue(payload, googleCallToActionValue)
                    handleChange(
                      { type: creativeActions.callToActionUpdate, payload: nextValue },
                      form,
                    )
                  }}
                  required={false} // For the dropdown. Otherwise a '*' is added when value is empty.
                  classNames="cta-dropdown"
                  disabled={isPublished || !canAnonEdit}
                  renderItem={renderCtaOption(metaCallToActionValue)}
                />
              )}
              {targetingPlatforms.includes(TARGETING_PLATFORMS.google) && (
                <Field
                  fieldName="googleCallToAction"
                  as={DropDown}
                  label={'google'}
                  bothSelected={hasBothPlatforms}
                  tooltip={{ content: 'Tips.Recommendations.CreativeComposer.CallToAction' }}
                  value={googleCallToActionValue}
                  options={sortedGoogleCtaOptions.map((x) => ({ value: x.value, text: x.text }))}
                  onChange={(payload) => {
                    const nextValue = buildCallToActionValue(metaCallToActionValue, payload)
                    handleChange(
                      { type: creativeActions.callToActionUpdate, payload: nextValue },
                      form,
                    )
                  }}
                  required={false}
                  classNames="cta-dropdown"
                  disabled={isPublished || !canAnonEdit}
                  renderItem={renderCtaOption(googleCallToActionValue)}
                />
              )}
            </div>
          </AccordionCard>

          <AccordionCard
            wrapperClassName="mb-3 creative-composer-section-wrapper creative-composer-section-wrapper__media"
            title={
              <InfoBox
                title="Media"
                body={
                  <>
                    <Trans
                      i18nKey="Recommendations.campaign.setup.media.info"
                      components={{
                        p: <p style={{ marginTop: '1rem' }} />,
                        a: (
                          <a
                            href={
                              SUPPORT_ARTICLE_URLS?.media?.[i18n.language] ||
                              SUPPORT_ARTICLE_URLS?.media?.en
                            }
                            target="_blank"
                            style={{ textDecoration: 'underline', display: 'inline-block' }}
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    />

                    {isPublished && (
                      <p className="mt-2">
                        {t('Recommendations.campaign.setup.media.infoPublished')}
                      </p>
                    )}
                  </>
                }
              >
                <h5 className="mb-0 d-inline-block">Media</h5>
              </InfoBox>
            }
          >
            {tabSwitch === 'carousel' ? (
              <CarouselAd
                cards={form.content.carousel_cards || []}
                onCarouselCardsChange={handleCarouselCardsChange}
                handleChange={handleChange}
                eventId={eventId}
                language={form?.content?.language}
                bodiesCount={Math.max(3, form?.content?.body?.length || 0)}
                onLanguageChange={handleCarouselLanguageChange}
                isOpen={isOpen}
                isRunning={isRunning}
                form={form}
                platforms={targetingPlatforms}
                campaignType={googleApiCampaignType}
              />
            ) : (
              <>
                <FieldError message={validationErrorsMedia.media || null} />

                <MediaUpload
                  eventId={eventId}
                  tc={campaign.tc}
                  tcRunId={campaign.tc_run_id}
                  headlines={(form?.content?.headlines || form?.content?.headline || []).filter(
                    Boolean,
                  )}
                  onClearValidationErrors={() =>
                    setValidationErrorsMedia((prev) => ({ ...prev, media: null }))
                  }
                  initialMedia={augmentIncomingCreativeMedia({
                    media: form.content.media || [],
                    placement_media: form.content.placement_media || [],
                  })}
                  onChange={(
                    files,
                    removedIndex = null,
                    hasPendingMedia = false,
                    errorMessages = [],
                    validFiles = true,
                    ...rest
                  ) => {
                    const generatedMediaCount = rest[rest.length - 1]
                    handleChange(
                      {
                        type: creativeActions.mediaAndPlacementUpdate,
                        payload: {
                          media: files,
                          placement_media: [],
                          generated_media_count: generatedMediaCount,
                        },
                        index: removedIndex,
                      },
                      form,
                      hasPendingMedia,
                      errorMessages,
                      validFiles,
                    )
                    if (errorMessages.length > 0) {
                      setValidationErrorsMedia((prev) => ({
                        ...prev,
                        media: errorMessages.map((err) => err.errors || []).flat(),
                      }))
                    } else {
                      // Clear errors when files are removed/added and there are no new errors
                      setValidationErrorsMedia((prev) => ({
                        ...prev,
                        media: null,
                      }))
                    }
                  }}
                  maxItems={MAX_MEDIA_ITEMS}
                  generatedMediaCount={form.content.generated_media_count ?? 0}
                  isCampaignRunning={isRunning}
                  onRequestHeadlines={scrollToHeadlineSection}
                  showYoutubeUrlInput={targetingPlatforms.includes(TARGETING_PLATFORMS.google)}
                  showValidationErrors={showValidationErrors}
                />
              </>
            )}
          </AccordionCard>
        </div>

        <div className="creative-composer-section-wrapper">
          <Targeting
            event={eventInfo}
            tc={campaign.tc}
            tc_run_id={campaign.tc_run_id}
            isFirstTc={isFirstTc}
            onChange={(targeting) => {
              // For editing a published campaign
              currentPublishedTargeting.current = targeting
            }}
            // savedAudienceSelected is deliberately NOT included here — it
            // used to be, but that made this one flag cascade into
            // MetaAudienceTargetingField/GoogleAudienceTargetingField too,
            // locking the very picker a user would need to change/clear
            // their saved-audience choice, and disabling Google's audience
            // field for a purely Meta-side (tcTargeting.fb.saved_audience_id)
            // concept it has nothing to do with. GeoTargetingField still
            // locks correctly on its own via the separate
            // savedAudienceSelected prop below (PostalCodes/RangeLocations
            // already read that directly, and its mode dropdown now does
            // too — see GeoTargetingField.jsx).
            // isPublished is deliberately separate too: publishing freezes the
            // targeting *mode*, not the location content or the audience.
            disabled={!canAnonEdit}
            isPublished={isPublished}
            savedAudienceSelected={savedAudienceSelected}
            showValidationErrors={!isOpen}
          />
        </div>

        <div>
          {form.loading || setupLoading ? (
            <div className="my-3">
              <LoadingSpinner data-testid="loading-spinner" title={t('common.sending')} />
            </div>
          ) : (
            <div className="creative-composer-controls-container">
              {isRunning ? (
                <div className="my-3">
                  <TransparentBgButton
                    text={t('common.cancel')}
                    onClick={() => {
                      dispatchFormAction({
                        type: OVERWRITE,
                        payload: { ...initStateObject, content: originalCreative },
                      })
                      if (onCancel) onCancel()
                    }}
                    color="navy"
                  />

                  <SecondaryButton
                    data-testid="save-campaign-form-btn"
                    text={t('common.update')}
                    onClick={(e) => {
                      e.preventDefault()
                      onUpdate()
                    }}
                    color="navy"
                    disabled={
                      form.loading ||
                      setupLoading ||
                      hasPendingMedia ||
                      !hasWaveAccess ||
                      !allFilesUploaded(form.content.media) ||
                      !validateCreative(form.content, {
                        setupOnly: false,
                        platform: derivePlatformSelectionFromSetup(setup),
                        campaignType: googleCampaignType,
                      }).valid ||
                      !hasEnabledVariations(setup.creatives) ||
                      !validateLocationTargeting(
                        currentPublishedTargeting.current,
                        campaign.tc,
                        campaign.tc_run_id,
                        hasCustomAudience,
                        savedAudienceSelected,
                        targetingPlatforms,
                        setup,
                      ).valid
                    }
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </form>

      <PortalModal isOpen={isConfirmModalOpen} onClickOutside={() => setIsConfirmModalOpen(false)}>
        <ConfirmationModal
          title={t(
            `Recommendations.campaign.setup.carouselAd.confirmSwitch.title.${tabSwitch === 'single' ? 'toCarousel' : 'toSingle'}`,
          )}
          description={t('Recommendations.campaign.setup.carouselAd.confirmSwitch.description')}
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={() => {
            setIsConfirmModalOpen(false)
            if (tabSwitch === 'single') {
              handleChange(
                {
                  type: creativeActions.switchToCarousel,
                },
                form,
              )
              setTabSwitch('carousel')
            } else {
              handleChange(
                {
                  type: creativeActions.carouselCardsSet,
                  payload: [],
                },
                form,
              )
              setTabSwitch('single')
            }
          }}
        />
      </PortalModal>
    </>
  )
}

CampaignForm.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  eventDate: PropTypes.string.isRequired,
  eventInfo: PropTypes.object,
  campaign: PropTypes.object.isRequired,
  goal: PropTypes.string,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
  onChange: PropTypes.func,
  showValidationErrors: PropTypes.bool,
  isFirstTc: PropTypes.bool,
}
